/**
 * S-08b d: JST 固定の datetime-local <-> ISO8601 変換ヘルパ (saas-admin 内 localize)。
 *
 * - AuditLogsPage の `startAt` / `endAt` を `<input type="datetime-local">` で受けるが、
 *   内部値は repository 互換性のため ISO8601 (Asia/Tokyo) 文字列を維持する
 * - JST 固定なので date-fns-tz は使わず native Date + 文字列操作で実装
 * - shared/src 昇格は将来 tenant-portal / tenant-fuel で同等 helper が必要になった時点で行う
 *   (WORKTREE-RULES §1.3 合流ファイル準拠 / YAGNI)
 */

const JST_OFFSET_MS = 9 * 60 * 60 * 1000;

/**
 * `<input type="datetime-local">` の値 (`YYYY-MM-DDTHH:mm` または秒付き) を
 * JST タイムゾーン付き ISO8601 (`YYYY-MM-DDTHH:mm:ss+09:00`) に変換する。
 *
 * - 空文字は空文字で返す (filter 未指定の表現)
 * - 秒が無ければ `:00` を補う
 * - 不正フォーマットは空文字を返す (上流の input type が validate するため通常通らない)
 */
export function datetimeLocalToIsoJst(value: string): string {
  if (!value) return '';
  const match = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(:\d{2})?(?:\.\d+)?$/.exec(value);
  if (!match) return '';
  const base = match[1];
  const sec = match[2] ?? ':00';
  return `${base}${sec}+09:00`;
}

/**
 * ISO8601 文字列 (任意の TZ) を `<input type="datetime-local">` の値 (`YYYY-MM-DDTHH:mm`) に
 * JST 表示として変換する。
 *
 * - 空文字 / 不正値は空文字を返す
 * - UTC や +00:00 で渡された値も JST 表示に揃える
 */
export function isoToDatetimeLocalJst(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const jst = new Date(d.getTime() + JST_OFFSET_MS);
  const yyyy = jst.getUTCFullYear();
  const mm = String(jst.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(jst.getUTCDate()).padStart(2, '0');
  const hh = String(jst.getUTCHours()).padStart(2, '0');
  const mi = String(jst.getUTCMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}
